/**
 * Device Breakdown Card - Shows analysis results broken down by device type
 */

import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Phone as PhoneIcon,
  DirectionsCar as VehicleIcon,
  Watch as WatchIcon,
  DeviceUnknown as UnknownIcon,
} from '@mui/icons-material';

interface DeviceBreakdownProps {
  deviceBreakdown: {
    [deviceType: string]: {
      // Old format (simple entity counts)
      entities?: string[];
      count?: number;
      
      // New format (multi-device comparison)
      total_dwell_locations?: number;
      total_routes?: number;
      total_points?: number;
      unique_entities?: number;
    };
  };
  totalEntities: number;
  isMultiDeviceComparison?: boolean; // Flag to indicate rich comparison format
}

type DeviceBreakdownEntry = DeviceBreakdownProps['deviceBreakdown'][string];

const DeviceBreakdownCard: React.FC<DeviceBreakdownProps> = ({ 
  deviceBreakdown, 
  totalEntities,
  isMultiDeviceComparison = false
}) => {
  const getDeviceIcon = (deviceType: string) => {
    switch (deviceType.toLowerCase()) {
      case 'phone':
        return <PhoneIcon />;
      case 'vehicle':
        return <VehicleIcon />;
      case 'watch':
        return <WatchIcon />;
      default:
        return <UnknownIcon />;
    }
  };

  const getDeviceColor = (deviceType: string): "primary" | "secondary" | "success" | "warning" | "error" => {
    switch (deviceType.toLowerCase()) {
      case 'phone':
        return 'primary';
      case 'vehicle': 
        return 'secondary';
      case 'watch':
        return 'success';
      default:
        return 'warning';
    }
  };

  if (!deviceBreakdown || Object.keys(deviceBreakdown).length === 0) {
    return null;
  }

  // Detect if this is multi-device comparison format
  const isRichComparison = isMultiDeviceComparison || (Object.values(deviceBreakdown) as DeviceBreakdownEntry[]).some(
    (data) => data.total_dwell_locations !== undefined
  );

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          📱 {isRichComparison ? 'Multi-Device Comparison Analysis' : 'Multi-Device Analysis Breakdown'}
          <Chip label={`${Object.keys(deviceBreakdown).length} device types`} size="small" color="primary" />
        </Typography>

        {/* Device Type Summary Cards */}
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {(Object.entries(deviceBreakdown) as [string, DeviceBreakdownEntry][]).map(([deviceType, data]) => {
            return (
              <Grid item xs={12} sm={6} md={4} key={deviceType}>
                <Card variant="outlined" sx={{ height: '100%' }}>
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Box sx={{ color: `${getDeviceColor(deviceType)}.main`, mb: 1 }}>
                      {getDeviceIcon(deviceType)}
                    </Box>
                    
                    {isRichComparison ? (
                      // Rich multi-device comparison format
                      <>
                        <Typography variant="h4" color={`${getDeviceColor(deviceType)}.main`}>
                          {data.total_dwell_locations || 0}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {deviceType}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {data.total_dwell_locations || 0} locations
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {data.total_routes || 0} routes
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          {data.total_points || 0} GPS points
                        </Typography>
                      </>
                    ) : (
                      // Simple entity count format
                      <>
                        <Typography variant="h4" color={`${getDeviceColor(deviceType)}.main`}>
                          {data.count || 0}
                        </Typography>
                        <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                          {deviceType}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {Math.round(((data.count || 0) / totalEntities) * 100)}% of entities
                        </Typography>
                      </>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>

        {/* Detailed Analysis Comparison Table */}
        {isRichComparison ? (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">
                Detailed Device Comparison Metrics
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Device Type</strong></TableCell>
                      <TableCell><strong>Dwell Locations</strong></TableCell>
                      <TableCell><strong>Routes</strong></TableCell>
                      <TableCell><strong>GPS Points</strong></TableCell>
                      <TableCell><strong>Unique Entities</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(Object.entries(deviceBreakdown) as [string, DeviceBreakdownEntry][]).map(([deviceType, data]) => (
                      <TableRow key={deviceType} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getDeviceIcon(deviceType)}
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {deviceType}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={data.total_dwell_locations || 0} 
                            color={getDeviceColor(deviceType)}
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={data.total_routes || 0} 
                            color={getDeviceColor(deviceType)}
                            size="small"
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {(data.total_points || 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="text.secondary">
                            {data.unique_entities || 0}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        ) : (
          // Original entity list format
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="subtitle2">
                Entity Details by Device Type
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper} variant="outlined">
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Device Type</strong></TableCell>
                      <TableCell><strong>Entity Count</strong></TableCell>
                      <TableCell><strong>Entity IDs</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(Object.entries(deviceBreakdown) as [string, DeviceBreakdownEntry][]).map(([deviceType, data]) => (
                      <TableRow key={deviceType} hover>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {getDeviceIcon(deviceType)}
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              {deviceType}
                            </Typography>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={data.count || 0} 
                            color={getDeviceColor(deviceType)}
                            size="small" 
                          />
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {(data.entities || []).slice(0, 5).map((entity) => (
                              <Chip 
                                key={entity} 
                                label={entity} 
                                size="small" 
                                variant="outlined"
                              />
                            ))}
                            {(data.entities || []).length > 5 && (
                              <Chip 
                                label={`+${(data.entities || []).length - 5} more`} 
                                size="small" 
                                color="secondary"
                              />
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        )}
      </CardContent>
    </Card>
  );
};

export default DeviceBreakdownCard;
